/**
 * Typed fetch wrapper for the Huroof API.
 *
 * Auto-injects the X-Device-Id header and the optional X-Parent-PIN. Throws
 * on non-2xx responses with a structured error so the SPA can render the
 * kid-friendly message returned by the server's error middleware.
 */

import { getOrCreateDeviceId } from './deviceId';

export interface ApiErrorShape {
  ok: false;
  code: number;
  kid_message_ar: string;
  kid_message_en: string;
  request_id?: string;
}

export class ApiError extends Error {
  readonly code: number;
  readonly kidMessageAr: string;
  readonly kidMessageEn: string;

  constructor(body: ApiErrorShape) {
    super(body.kid_message_en);
    this.code = body.code;
    this.kidMessageAr = body.kid_message_ar;
    this.kidMessageEn = body.kid_message_en;
  }
}

export interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  parentPin?: string;
  signal?: AbortSignal;
}

export async function api<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body, parentPin, signal } = options;

  const headers: Record<string, string> = {
    'X-Device-Id': getOrCreateDeviceId(),
  };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (parentPin) headers['X-Parent-PIN'] = parentPin;

  const res = await fetch(path, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

  if (!res.ok) {
    const err = (await res.json().catch(() => null)) as ApiErrorShape | null;
    throw new ApiError(
      err ?? {
        ok: false,
        code: res.status,
        kid_message_ar: 'حَاوِلْ مَرَّةً أُخْرَى!',
        kid_message_en: "Let's try that again!",
      },
    );
  }

  return res.json() as Promise<T>;
}
